import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import tenantAdminService from '../../services/tenantAdminService';
import { TenantAdmin, TenantMigrateToWebJob } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

interface TenantLegacyMigrationDialogProps {
  open: boolean;
  tenant: TenantAdmin | null;
  jobId: string | null;
  onClose: () => void;
  onSucceeded: (tenant: TenantAdmin) => void;
}

const POLL_MS = 1500;

const TenantLegacyMigrationDialog: React.FC<TenantLegacyMigrationDialogProps> = ({
  open,
  tenant,
  jobId,
  onClose,
  onSucceeded,
}) => {
  const [job, setJob] = useState<TenantMigrateToWebJob | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [notifiedSuccess, setNotifiedSuccess] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const running = job?.status === 'Pending' || job?.status === 'Running';
  const succeeded = job?.status === 'Succeeded';
  const failed = job?.status === 'Failed';

  useEffect(() => {
    if (!open) {
      setJob(null);
      setPollError(null);
      setNotifiedSuccess(false);
      setDetailsOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !jobId) return undefined;

    let cancelled = false;
    let timeoutId = 0;

    const poll = async () => {
      try {
        const next = await tenantAdminService.getMigrateJob(jobId);
        if (cancelled) return;
        setJob(next);
        setPollError(null);
        if (next.status === 'Pending' || next.status === 'Running') {
          timeoutId = window.setTimeout(() => {
            void poll();
          }, POLL_MS);
        }
      } catch (error: unknown) {
        if (cancelled) return;
        setPollError(getApiErrorMessage(error, 'No se pudo consultar el avance de la migración.'));
        timeoutId = window.setTimeout(() => {
          void poll();
        }, POLL_MS);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [open, jobId]);

  useEffect(() => {
    if (!succeeded || !tenant || notifiedSuccess) return;
    setNotifiedSuccess(true);
    onSucceeded(tenant);
  }, [succeeded, tenant, notifiedSuccess, onSucceeded]);

  const progress = useMemo(() => {
    const steps = job?.steps ?? [];
    if (steps.length === 0) return 0;
    const done = steps.filter((step) => step.status === 'Succeeded').length;
    return Math.round((done / steps.length) * 100);
  }, [job]);

  const showErrorDetails = failed && Boolean(job?.error || job?.errorDetail);

  return (
    <>
      <Dialog
        open={open}
        onClose={running || detailsOpen ? undefined : onClose}
        disableEscapeKeyDown={running || detailsOpen}
        fullWidth
        maxWidth="sm"
      >
      <DialogTitle>Migrar tenant {tenant?.code ?? ''} a eOne</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Se actualiza el esquema de la base del tenant. No cierre esta ventana hasta que termine.
          </Typography>
          {(job?.isSecondary || tenant?.parentTenantCode) && (
            <Alert severity="info">
              BD hija de {job?.parentTenantCode || tenant?.parentTenantCode}
              {job?.parentDatabaseName ? ` → ${job.parentDatabaseName}` : ''}. Los maestros
              permanecen como vistas a la madre.
            </Alert>
          )}

          <Box>
            <LinearProgress
              variant={running && progress === 0 ? 'indeterminate' : 'determinate'}
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {progress}% completado
            </Typography>
          </Box>

          {succeeded && (
            <Alert severity="success">Migración completada correctamente.</Alert>
          )}
          {failed && (
            <Alert
              severity="error"
              action={
                showErrorDetails ? (
                  <Button color="inherit" size="small" onClick={() => setDetailsOpen(true)}>
                    Ver detalles
                  </Button>
                ) : undefined
              }
            >
              {job?.error || 'La migración falló.'}
            </Alert>
          )}
          {pollError && !failed && (
            <Alert severity="warning">{pollError}</Alert>
          )}

          <List dense>
            {(job?.steps ?? []).map((step) => (
              <ListItem
                key={step.key}
                alignItems="flex-start"
                sx={{ px: 0 }}
                secondaryAction={
                  step.status === 'Failed' && showErrorDetails ? (
                    <Button size="small" onClick={() => setDetailsOpen(true)}>
                      Ver detalles
                    </Button>
                  ) : undefined
                }
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                  {step.status === 'Succeeded' && <CheckCircleIcon color="success" fontSize="small" />}
                  {step.status === 'Failed' && <ErrorIcon color="error" fontSize="small" />}
                  {step.status === 'Running' && <CircularProgress size={18} />}
                  {(step.status === 'Pending' || !step.status) && (
                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={step.title}
                  secondary={step.message}
                  primaryTypographyProps={{
                    fontWeight: step.status === 'Failed' ? 600 : 500,
                    color: step.status === 'Failed' ? 'error.main' : 'text.primary',
                  }}
                  secondaryTypographyProps={{
                    pr: step.status === 'Failed' && showErrorDetails ? 14 : 0,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={detailsOpen}
      onClose={() => setDetailsOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Detalle del error</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {job?.error}
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 360,
            overflow: 'auto',
          }}
        >
          {job?.errorDetail || job?.error}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDetailsOpen(false)} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TenantLegacyMigrationDialog;
